import { $, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu } from '../lib/select-menu.js';

/**
 * The starboard editor.
 *
 * Two switches here default to something other than "off": the anti-abuse is on
 * and the post survives a drop in reactions. A blank form has to be born in the
 * state the guide describes, or the first rule created contradicts the page
 * that sold it.
 */

interface Board {
  _id: string;
  name: string;
  emoji: string;
  threshold: number;
  channelId: string;
  selfStarAllowed: boolean;
  keepWhenBelow: boolean;
  excludedChannelIds: string[];
  excludedRoleIds: string[];
  enabled: boolean;
}

interface EditorPageData {
  starboard: Board;
  guildId: string;
}

export function initStarboardEditorPage(): void {
  const form = $<HTMLFormElement>('[data-starboard-form]');
  if (!form) return;

  const { starboard, guildId } = pageData<EditorPageData>();

  fillForm(form, starboard as unknown as Record<string, unknown>);

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = starboard.channelId ? [starboard.channelId] : [];

  for (const field of ['excludedChannelIds', 'excludedRoleIds'] as const) {
    const menu = findSelectMenu(form, field);
    if (menu) menu.value = starboard[field];
  }

  // `fillForm` can guess nothing for a creation: the document does not exist
  // yet, and three unticked boxes would make a starboard that protects nothing
  // and publishes nowhere.
  const fresh = !starboard._id;
  toggle(form, 'enabled', fresh ? true : starboard.enabled);
  toggle(form, 'keepWhenBelow', fresh ? true : starboard.keepWhenBelow);
  toggle(form, 'selfStarAllowed', fresh ? false : starboard.selfStarAllowed);

  mountResourceEditor({
    endpoint: 'starboard',
    listUrl: `/modules/starboard?guildId=${guildId}`,
    guildId,
    form,
    itemId: starboard._id,

    serialize: () => ({
      name: text(form, 'name'),
      emoji: text(form, 'emoji') || '⭐',
      threshold: Number(text(form, 'threshold')) || 3,
      channelId: findSelectMenu(form, 'channelId')?.single ?? '',
      selfStarAllowed: checked(form, 'selfStarAllowed'),
      keepWhenBelow: checked(form, 'keepWhenBelow'),
      excludedChannelIds: findSelectMenu(form, 'excludedChannelIds')?.value ?? [],
      excludedRoleIds: findSelectMenu(form, 'excludedRoleIds')?.value ?? [],
      enabled: checked(form, 'enabled'),
      publish: false,
    }),

    labels: {
      saved: ct('starboardSaved', 'Starboard saved.'),
      confirmDelete: ct('confirmDeleteStarboard', 'Delete this starboard?'),
      deleted: ct('starboardDeleted', 'Starboard deleted.'),
    },
  });
}

function toggle(form: HTMLFormElement, name: string, on: boolean): void {
  const field = form.elements.namedItem(name);
  if (field instanceof HTMLInputElement) field.checked = on;
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value.trim()
    : '';
}

function checked(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
